import { AxiosInstance } from 'axios';

import { ServiceOrder } from '@/model/ServiceOrder/ServiceOrder';
import { ServiceOrderItem } from '@/model/ServiceOrder/ServiceOrderItem';

import ServiceOrderApi from './ServiceOrderApi';

export class ServiceOrderRepository {
  serviceOrderApi: ServiceOrderApi;

  constructor(axiosInstance: AxiosInstance) {
    this.serviceOrderApi = new ServiceOrderApi(axiosInstance);
  }

  async checkServiceOrderForTicket(ticketId: number, serviceOrderId?: number): Promise<boolean> {
    const response = await this.serviceOrderApi.getServiceOrderByTicket(ticketId);
    const { results } = response;

    if (!Array.isArray(results)) {
      return false;
    }

    const resultWithoutActualSO = results.filter(result => result.id !== serviceOrderId);

    return resultWithoutActualSO.length > 0;
  }

  saveServiceOrderItem(item: Partial<ServiceOrderItem>): Promise<ServiceOrderItem> {
    const toSave = {
      ...item,
      package_item:
        typeof item.package_item === 'number' ? item.package_item : item.package_item?.id,
    };

    return this.serviceOrderApi.saveServiceOrderItem(toSave);
  }
  updateServiceOrderItem(item: Partial<ServiceOrderItem>): Promise<ServiceOrderItem> {
    const toUpdate = {
      ...item,
      package_item:
        typeof item.package_item === 'number' ? item.package_item : item.package_item?.id,
    };

    return this.serviceOrderApi.updateServiceOrderItem(toUpdate);
  }
  deleteServiceOrderItem(item: Partial<ServiceOrderItem>): Promise<ServiceOrderItem> {
    return this.serviceOrderApi.deleteServiceOrderItem(item);
  }

  saveServiceOrderItems(
    items: Partial<ServiceOrderItem>[],
    serviceOrderId: number,
  ): Promise<ServiceOrderItem[]> {
    const itemsWithSO = items.map(item => ({
      ...item,
      service_order: serviceOrderId,
    }));
    const newItems = itemsWithSO.filter(item => !item.id);
    const deletedItems = itemsWithSO.filter(item => item.id && item.toDelete);
    const updatedItems = itemsWithSO.filter(item => item.id && !item.toDelete && item.toUpdate);
    const newItemsPromises = newItems.map(item => this.saveServiceOrderItem(item));
    const deletedItemsPromises = deletedItems.map(item => this.deleteServiceOrderItem(item));
    const updatedItemsPromises = updatedItems.map(item => this.updateServiceOrderItem(item));

    return Promise.all([...newItemsPromises, ...deletedItemsPromises, ...updatedItemsPromises]);
  }

  async getServiceOrderItems(serviceOrderId: number): Promise<ServiceOrderItem[]> {
    try {
      const response = await this.serviceOrderApi.getServiceOrderItems(serviceOrderId);

      if (Array.isArray(response)) {
        return response;
      }

      if (response?.results) {
        return response.results;
      }

      return response?.data ?? [];
    } catch (error) {
      console.error('Error fetching service order items:', error);
      throw error;
    }
  }
  async updateServiceOrder(serviceOrder: Partial<ServiceOrder>): Promise<ServiceOrder> {
    return this.serviceOrderApi.updateServiceOrder(serviceOrder);
  }

  async uploadBillFile(serviceOrderId: number, file: File): Promise<ServiceOrder> {
    return this.serviceOrderApi.uploadBillFile(serviceOrderId, file);
  }

  generatePublicLink(serviceOrderId: number): Promise<string> {
    return this.serviceOrderApi.generatePublicLink(serviceOrderId);
  }
}
